/**
 * AZ ÜGYFÉL EGYETLEN MUNKAKÉPERNYŐJE — „mit kérnek tőlem".
 *
 * Kemény szabály, nem egyszerűsítés (`CLAUDE.md` §2): az ügyfélnek EGY
 * képernyője van, és ez az. A feltöltés MŰVELET rajta, nem oldal — ezért
 * nincs fülsor, nincs menü, és nincs külön „feltöltés" nézet.
 *
 * A MAKETT A MÉRCE: `docs/tervezes/design/Ugyfel.dc.html` (mobil) és
 * `UgyfelAsztal.dc.html` (szélesebb). Három sáv:
 *
 *   fejléc   — a CÉG neve, akinek a nevében feltölt. Nem a felhasználóé: a
 *              bizonylat a céghez tartozik, nem a személyhez.
 *   törzs    — a kérések, majd a feltöltések nyugtája
 *   lábsáv   — EGY művelet: „feltöltök valamit", akkor is, ha nem kértük
 *
 * A LÁBSÁV NEM FÜLSOR. Egyetlen művelet van rajta, és az a `2.4d` szerinti
 * helyzetre való: az ügyfél üzemanyagszámlát vagy parkolójegyet tölt fel,
 * amiről a NAV-nak nincs adata, tehát senki nem kérte be. Ha csak a kérésekre
 * lehetne válaszolni, ezek a bizonylatok soha nem érkeznének be.
 *
 * ⚠️ A LÁBSÁVBAN KÉT BELÉPÉSI PONT VAN, DE EGY MŰVELET. Az ejtőmező
 * (`DropZone`) csak a 44rem töréspont FÖLÖTT látszik — asztalon az a csoportos
 * feltöltés útja —, a gomb pedig alatta. Nem két funkció: ugyanaz a
 * `uploader.send` fut mindkettőről.
 *
 * ⚠️ ÉS MINDKETTŐ KÉRDEZ, CSAK MÁS SORRENDBEN (#85). A gomb egy lapot nyit
 * (`UploadSheet`): előbb „Mi ez?", aztán fotó vagy fájl. Az ejtőmezőre a fájl
 * ELŐBB érkezik — ott a köteg a panelen vár (`BatchPanel`), amíg az ügyfél
 * egyszer, az egészre válaszol. A kérés-kártya nem kérdez: ott a könyvelő
 * már megmondta, mit kér.
 */

import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UploadIcon } from '@/components/common';
import { useAuth } from '@/features/auth/AuthProvider';
import { capturePrincipal, freeCaptureKey } from '@/features/capture/session-storage';
import { InstallBanner } from '@/features/install/InstallBanner';
import { BatchPanel } from '@/features/uploads/BatchPanel';
import { DropZone } from '@/features/uploads/DropZone';
import type { ClientDocType } from '@/features/uploads/types';
import { UploadNotice } from '@/features/uploads/UploadNotice';
import { UploadReceipts } from '@/features/uploads/UploadReceipts';
import { UploadSheet } from '@/features/uploads/UploadSheet';
import { type UploaderState, useUploader } from '@/features/uploads/useUploader';
import { useUploads } from '@/features/uploads/useUploads';
import { RequestCard } from './RequestCard';
import { useRequests } from './useRequests';

export function RequestScreen() {
  const { t } = useTranslation();
  const { me, signOut } = useAuth();
  // Maszkolt munkamenetben (#76) a könyvelő ül a gépnél — neki nem ajánljuk
  // fel, hogy az ügyfél appját az iroda gépére telepítse.
  const impersonating = me?.actor.kind === 'impersonation';

  const requests = useRequests();
  const uploads = useUploads();

  const reloadRequests = requests.reload;
  const reloadUploads = uploads.reload;
  const refresh = useCallback(() => {
    // MINDKETTŐ frissül. A kérések listája azért, mert a könyvelő időközben
    // lezárhatott egy tételt; a nyugta azért, mert épp most jött be egy fájl.
    reloadRequests();
    reloadUploads();
  }, [reloadRequests, reloadUploads]);

  const uploader = useUploader(refresh);
  const items = requests.list?.items ?? [];

  return (
    <div className="shell">
      <header>
        {/* A MONOGRAM A CÉGÉ, nem a személyé — ugyanazért, amiért a fejléc
            neve is (lásd fent). Dísz: a név mellette áll, tehát a
            képernyőolvasónak nem mond újat.

            ⚠️ CSAK A CÉG NEVÉVEL JELENIK MEG. Az alkalmazásnévre visszaesve
            töltés közben egy idegen betű villant fel, ami a név megérkezésekor
            átváltott — a jel inkább hiányozzon, mint hogy tévesen mutasson. */}
        {requests.list ? (
          <span className="monogram" aria-hidden="true">
            {monogram(requests.list.company.name)}
          </span>
        ) : null}
        <span className="company">
          {/* A cég neve akkor is megvan, ha nincs egy kérés sem — a szerver
              külön adja, nem a sorokból vezetjük le. */}
          <strong>{requests.list?.company.name ?? t('app.name')}</strong>
          <span className="meta company-sub">{t('app.name')}</span>
        </span>
        <span className="spacer" />
        <button type="button" onClick={signOut}>
          {t('app.signOut')}
        </button>
      </header>

      <main>
        {requests.status === 'loading' ? <p className="meta">{t('requests.loading')}</p> : null}

        {requests.status === 'failed' ? (
          // A HIBÁT KI KELL MONDANI. Egy üres lista azt állítaná, hogy nincs
          // bekérve semmi — az ügyfél megnyugodna, a hiány meg ott maradna.
          <p className="error" role="alert">
            {t('requests.failed')}
          </p>
        ) : null}

        {requests.status === 'ready' ? (
          <>
            <h1>
              {items.length > 0
                ? t('requests.headline', { count: items.length })
                : t('requests.title')}
            </h1>

            {items.length === 0 ? <p className="empty">{t('requests.empty')}</p> : null}

            {items.map((request) => (
              <RequestCard
                key={request.id}
                request={request}
                busy={uploader.busy}
                onPick={uploader.send}
                onPages={uploader.sendPages}
                onGroups={(groups) => void uploader.sendGroups(groups)}
              />
            ))}

            {/* A KÉRÉSEK UTÁN, nem fölöttük: amit a könyvelő kér, az az első.
                És csak betöltés után — töltés közben a sáv a kérések helyén
                állna, majd lecsúszna alájuk. */}
            {impersonating ? null : <InstallBanner />}
          </>
        ) : null}

        <UploadReceipts
          uploads={uploads.uploads}
          hasMore={uploads.hasMore}
          loadingMore={uploads.loadingMore}
          docTypes={uploads.docTypes}
          onLoadMore={uploads.loadMore}
          onDeclared={uploads.markDeclared}
        />
      </main>

      <UploadFooter
        uploader={uploader}
        allowed={uploads.docTypes}
        // A KÉRETLEN SOROZAT KULCSA — és az IDENTITÁSÉ (#76): a maszkolt
        // könyvelő nem folytathatja az ügyfél félbehagyott fotózását.
        storageKey={me ? freeCaptureKey(capturePrincipal(me.actor)) : null}
      />
    </div>
  );
}

/**
 * A LÁBSÁV — a kéretlen feltöltés két belépési pontja (#85).
 *
 * Külön komponens, mert a saját állapota van: a mobil lap nyitottsága és az
 * asztalon behúzott, választásra váró köteg. A munkaképernyő ezekről nem
 * tud, és nem is kell tudnia.
 */
function UploadFooter({
  uploader,
  allowed,
  storageKey,
}: {
  uploader: UploaderState;
  allowed: readonly ClientDocType[] | null;
  storageKey: string | null;
}) {
  const { t } = useTranslation();
  /** A mobil feltöltő-lap nyitva van-e. */
  const [sheetOpen, setSheetOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const closeSheet = useCallback(() => {
    setSheetOpen(false);
    // A FÓKUSZ VISSZAJÖN oda, ahonnan a lap nyílt — különben a `<body>`-ra
    // esne, és a billentyűzetes ügyfél a képernyő elejéről kezdhetné újra.
    requestAnimationFrame(() => trigger.current?.focus());
  }, []);
  /** Az asztalon behúzott köteg, ami a „Mi ez?"-re vár. `null` = nincs. */
  const [batch, setBatch] = useState<File[] | null>(null);

  return (
    <>
      <footer>
        <UploadNotice outcome={uploader.outcome} onCancel={uploader.cancel} />

        {batch === null ? (
          <DropZone disabled={uploader.busy} onPick={setBatch} />
        ) : (
          <BatchPanel
            files={batch}
            allowed={allowed}
            busy={uploader.busy}
            onSend={(docType) => {
              const files = batch;
              setBatch(null);
              void uploader.send(files, docType);
            }}
            onCancel={() => setBatch(null)}
          />
        )}

        {/* A GOMB ÉS AZ EJTŐMEZŐ KIZÁRJA EGYMÁST, nem egymás mellett állnak.
          Asztalon az ejtőmezőnek SAJÁT gombja van („Válasszak a gépemről"),
          tehát ez itt egy második, ugyanoda vezető gomb lenne — a makett
          (`design/UgyfelAsztal.dc.html`) is egyet rajzol. A váltás CSS-ben
          van, a `.dropzone`-nal szimmetrikusan: ott a töréspont KAPCSOL BE,
          itt KIKAPCSOL. */}
        <div className="footer-pick">
          <button
            ref={trigger}
            type="button"
            className="pick pick-secondary"
            disabled={uploader.busy}
            onClick={() => setSheetOpen(true)}
          >
            <span className="pick-icon">
              <UploadIcon />
            </span>
            <span>{t('upload.anything')}</span>
          </button>
        </div>
        <p className="meta centered-text">{t('upload.anythingHint')}</p>
      </footer>

      {sheetOpen ? (
        <UploadSheet
          allowed={allowed}
          storageKey={storageKey}
          onClose={closeSheet}
          onFiles={(files, docType) => void uploader.send(files, docType)}
          onPages={(pages, docType) => void uploader.sendPages(pages, docType)}
          onGroups={(groups, docType) => void uploader.sendGroups(groups, docType)}
        />
      ) : null}
    </>
  );
}

/**
 * A cégnév első betűje, nagybetűvel. EGY betű, nem kettő: a „Szegedi
 * Építőipari" két kezdőbetűje „SÉ" lenne, a magyar kettős betűk („Sz",
 * „Gy") pedig egy kétbetűs monogramot rendszeresen félbevágnának.
 */
function monogram(name: string): string {
  return name.trim().charAt(0).toLocaleUpperCase('hu');
}
